import type { ImageLookupType } from '../images/imageLookUps';

export interface SubjectImageUpdateModel {
  cropX: number;
  cropY: number;
  xMid: number;
  yMid: number;
  rotation: number;
}

export function isSubjectImage(
  image: SubjectImageDisplayModel | GroupImageDisplayModel,
): image is SubjectImageDisplayModel {
  return !!(image as SubjectImageDisplayModel).subjectId;
}

export interface SubjectImageDisplayModel {
  id: number;
  /**
   * PRESET-OWNERSHIP stamps, NOT the group-photo discriminator (that's `imageLookupType ===
   * 'hosted-group'` — see `isGroupSubjectImage`). Only a group APPLIED to the subject (applied preset
   * or assigned-to-everyone group) can own a row: set when preset machinery materializes it or adopts
   * a matching manual one. A preset change removes rows by these stamps, and the delete guard blocks
   * removing rows the applied preset still owns. A MANUALLY (re-)assigned group photo carries null in
   * both.
   */
  owningGroupId?: number;
  /** The GroupImage slot a preset materialized this row from. See `owningGroupId`. */
  createdFromGroupImageId?: number;

  groupName?: string;

  filename: string;
  originalImageName: string;
  subjectPasscode: string;
  cropX: number;
  cropY: number;
  xMid: number;
  yMid: number;
  rotation: number;
  imageUrl: string;
  imageThumbnailUrl: string;
  height: number | null;
  width: number | null;
  ppi: number | null;
  subjectId: number;
  isYearBookPose?: boolean;
  imageLookupId?: number;
  imageLookupType?: ImageLookupType;
  /**
   * Front end only
   * This is the name of the subject that the image belongs to
   * Used for searching purposes
   */
  subjectName: string;
  watermark?: any;
}

/**
 * What a group image create/update did to existing carts (server: RepointSummary). Only present on the
 * Create/Update response when the image became the preset's primary image and cart group nodes were
 * re-pointed at it. Informational: nothing on the client acts on it today (the carts themselves are
 * refetched after the change, and Entry's Verify eye shows the carts moved).
 */
export interface GroupImageRepointSummary {
  repointedPackageCount: number;
  repointedCartCount: number;
  /** The packages the re-point changed; a package that already showed the image is not on this list. */
  repointedCartPackageIds: number[];
}

export interface GroupImageDisplayModel {
  id: number;
  imageName: string;
  originalImageName: string;
  identifier: string;
  imageUrl: string;
  imageThumbnailUrl: string;
  height: number | null;
  width: number | null;
  pPI: number | null;
  groupId: number;
  /** Front end only
   * This is the name of the group that the image belongs to
   * Used for searching purposes
   */
  groupName: string;
  imageLookupId?: number;
  cropX: number;
  cropY: number;
  xMid: number;
  yMid: number;
  rotation: number;
  isPrimaryImage?: boolean;
  watermark?: any;
  /** Response-only; see GroupImageRepointSummary. Null/undefined when no re-point ran. */
  repointSummary?: GroupImageRepointSummary | null;
}

export interface SubjectImageCreateModel {
  filename: string;
  originalImageName: string;
  subjectPasscode: string;
  cropX: number;
  cropY: number;
  xMid: number;
  yMid: number;
  rotation: number;
  imageThumbnailUrl: string;
  height: number | null;
  width: number | null;
  pPI: number | null;
  subjectId: number;
  sortIndex: number;
}

export interface GroupImageUpdateModel {
  imageLookupId: number | null;
  cropX: number;
  cropY: number;
  xMid: number;
  yMid: number;
  rotation: number;
  isPrimaryImage: boolean;
}

export interface GroupImageCreateModel {
  sortIndex: number;
  originalImageName: string;
  imageIdentifier: string;
  name: string;
  imageLookUpId: number;
  cropX: number;
  cropY: number;
  xMid: number;
  yMid: number;
}

export const isGroupImage = (
  image: SubjectImageDisplayModel | GroupImageDisplayModel,
): image is GroupImageDisplayModel => {
  return (image as GroupImageDisplayModel).groupId !== undefined;
};

/**
 * A group photo as it appears on a subject. Classified primarily by the assigned image's OWN lookup
 * type (`hosted-group`) — the same signal the backend uses to tell group from individual
 * (`SubjectImageCommandService` keys on `ImageLookup.Type == GROUP`). A set `owningGroupId` is also
 * treated as group so preset/team images (tagged via the studio entry/bulk-map endpoints) still count.
 *
 * Keying on the lookup type — not `owningGroupId` alone — is what correctly classifies a MANUALLY
 * assigned group photo whose `owningGroupId` was never tagged (e.g. a loose group image that isn't a
 * registered GroupImage of the job). Previously those leaked into the individual picker and were
 * eligible for ImageX nodes instead of being handled as group photos.
 */
export const isGroupSubjectImage = (image: SubjectImageDisplayModel) =>
  image.imageLookupType === 'hosted-group' || image.owningGroupId != null;

/**
 * An individual portrait: backed by a real image lookup and NOT a group photo (see
 * `isGroupSubjectImage`). Mirrors the "Pose 1" definition used by the gallery subject picker.
 */
export const isIndividualSubjectImage = (image: SubjectImageDisplayModel) =>
  image.imageLookupId != null && !isGroupSubjectImage(image);

export const subjectHasIndividualImage = (images: SubjectImageDisplayModel[] | undefined | null) =>
  !!images?.some(isIndividualSubjectImage);

export const subjectHasGroupImage = (images: SubjectImageDisplayModel[] | undefined | null) =>
  !!images?.some(isGroupSubjectImage);

export const acceptedImageTypeString = '.jpg, .jpeg, .png';
export const acceptedImageTypeArray = ['jpg', 'jpeg', 'png'];

export const uploadRestrictions = {
  acceptedFileTypes: acceptedImageTypeArray,
  maxFileSize: 28 * 1024 * 1024, // 28 MB. Update display below if this changes
  maxFileSizeDisplay: '28 MB',
};
